import classNames from 'classnames';

import {
  CTA_TYPE,
  Heading,
  HEADING_SIZE,
  HEADING_TAG,
  ListComponentV3,
  LIST_VARIANT,
  MainBanner,
  PageSection,
  SecondaryBanner,
  SECONDARY_BANNER_VARIANT,
  SECTION_THEME,
  useDeviceSize,
  BentoCards,
  AdvocatesComponent,
} from '@amzn/sss-website-theme';

import { ROUTES } from 'src/enums/Routes';
import { getImageRoot, t } from 'src/helpers';

// eslint-disable-next-line no-restricted-imports
import styles from '../Btf.module.scss';

enum SECTION_IDS {
  CARBON_STANDARD = 'carbon-standard',
  MEASUREMENT = 'measurement',
}

interface CoteDivoireCreditBtfTemplateProps {
  productDetailsBundle: any;
  commonBundle: any;
  segmentPageBundle: any;
}

export const CoteDivoireCreditBtfTemplate = ({
  productDetailsBundle,
  commonBundle,
  segmentPageBundle,
}: CoteDivoireCreditBtfTemplateProps) => {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  return (
    <>
      <PageSection theme="squid-ink" className={classNames(styles.pageSection)}>
        <BentoCards
          className={styles.bentoCards}
          isRebrandingActive
          cardsWrapperProps={{
            openNewTabLabel: t(productDetailsBundle, 'newTab_label'),
            sliderPaginationLabel: t(productDetailsBundle, 'of_label'),
            sliderNextAriaLabel: t(productDetailsBundle, 'nextSlide_ariaLabel'),
            sliderPreviousAriaLabel: t(productDetailsBundle, 'previousSlide_ariaLabel'),
          }}
          sourceLabel={t(productDetailsBundle, 'source_label')}
          hideImageCardsOnMobile
          description={t(productDetailsBundle, 'coteDivoire_benefits_description')}
          title={t(productDetailsBundle, 'coteDivoire_benefits_title')}
          firstCard={{
            src: `${imageRoot}/cdi-social1.png`,
            alt: t(productDetailsBundle, 'coteDivoire_benefits_firstCard_alt'),
          }}
          secondCard={{
            src: `${imageRoot}/cdi-social2.png`,
            alt: t(productDetailsBundle, 'coteDivoire_benefits_secondCard_alt'),
          }}
          theme={SECTION_THEME.Dark}
        />
      </PageSection>

      <PageSection className={classNames(styles.pageSection)} theme="medium-spruce" id={SECTION_IDS.CARBON_STANDARD}>
        <SecondaryBanner
          isRebrandingActive
          imagePrimary={{
            src: `${imageRoot}/cdi-method1.png`,
            alt: t(productDetailsBundle, 'coteDivoire_methodology_imagePrimary'),
          }}
          imageSecondary={{
            src: `${imageRoot}/cdi-method2.png`,
            alt: t(productDetailsBundle, 'coteDivoire_methodology_imageSecondary'),
          }}
          title={t(productDetailsBundle, 'coteDivoire_methodology_title')}
          description={t(productDetailsBundle, 'coteDivoire_methodology_description')}
          theme={SECTION_THEME.Sustainability_Spruce}
          cta={{
            label: t(productDetailsBundle, 'carbonStandard_cta'),
            link: 'https://www.forestcarbonpartnership.org/fcpf-standard/',
          }}
          variant={SECONDARY_BANNER_VARIANT.LEFT}
        />
      </PageSection>

      <PageSection id={SECTION_IDS.MEASUREMENT} theme="light-spruce">
        <MainBanner
          isRebrandingActive
          image={{
            src: `${imageRoot}/cdi-mrv.png`,
            alt: t(productDetailsBundle, 'coteDivoire_measurement_alt'),
          }}
          theme={SECTION_THEME.Sustainability_Spruce}
          title={t(productDetailsBundle, 'coteDivoire_measurement_title')}
          subtitle={t(productDetailsBundle, 'coteDivoire_measurement_description')}
          accordions={[
            {
              title: t(productDetailsBundle, 'coteDivoire_measurement_accordionOne_title'),
              content: [
                {
                  text: t(productDetailsBundle, 'coteDivoire_measurement_accordionOne_description'),
                },
              ],
            },
            {
              title: t(productDetailsBundle, 'coteDivoire_measurement_accordionTwo_title'),
              content: [
                {
                  text: t(productDetailsBundle, 'coteDivoire_measurement_accordionTwo_description'),
                },
              ],
            },
            {
              title: t(productDetailsBundle, 'coteDivoire_measurement_accordionThree_title'),
              content: [
                {
                  text: t(productDetailsBundle, 'coteDivoire_measurement_accordionThree_description'),
                },
              ],
            },
          ]}
        />
      </PageSection>
      <PageSection theme="light-spruce">
        <Heading className={styles.learnMoreHeading} as={HEADING_TAG.H3} size={HEADING_SIZE.h3}>
          {t(productDetailsBundle, 'coteDivoire_learnMoreHeading')}
        </Heading>
        <ListComponentV3
          theme={SECTION_THEME.Light_Spruce}
          items={[
            {
              title: t(productDetailsBundle, 'teaser_reducingDeforestation'),
              subtitle: t(productDetailsBundle, 'reducingDeforestation_subtitle'),
              image: {
                src: `${imageRoot}/credits-segments-deforestation-hero-1.jpg`,
                alt: t(productDetailsBundle, 'teaser_reducingDeforestation_alt'),
              },
              cta: {
                href: `${ROUTES.CREDITS_SEGMENT_REDUCE_DEFORESTATION}`,
                type: CTA_TYPE.Link,
                forceServerSideNav: true,
              },
              label: t(productDetailsBundle, 'learnMore_exploreScience'),
              theme: SECTION_THEME.Light_Spruce,
              variant: LIST_VARIANT.Accordion,
              exploreCopy: t(productDetailsBundle, 'learnMore_explore'),
              newTabCopy: t(productDetailsBundle, 'learnMore_newTab'),
              pdfCopy: t(productDetailsBundle, 'learnMore_pdf'),
            },
          ]}
        />
      </PageSection>
      <AdvocatesComponent
        dataTrackingLabel={t(segmentPageBundle, 'advocates_dataTrackingLabel')}
        theme={SECTION_THEME.Squid_Ink}
        isStandAlone
        brandTabs={{
          buttonsAriaLabels: {
            previous: t(commonBundle, 'previous_ariaLabel'),
            next: t(commonBundle, 'next_ariaLabel'),
          },
          options: [
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-3.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_krupp'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-3.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_krupp'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_krupp'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_krupp'),
            },
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-2.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_figueres'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-2.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_figueres'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_figueres'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_figueres'),
            },
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-1.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_sanjayan'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-1.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_sanjayan'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_sanjayan'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_sanjayan'),
            },
          ],
        }}
        quotes={[
          t(segmentPageBundle, 'advocates_quote_sanjayan'),
          t(segmentPageBundle, 'advocates_quote_figueres'),
          t(segmentPageBundle, 'advocates_quote_krupp'),
        ]}
        backgroundImage={{
          src: `${imageRoot}/credits-advocates-1.jpeg`,
          alt: t(segmentPageBundle, 'advocates_backgroundImage_altText'),
        }}
      />
    </>
  );
};
